import { useCallback, useRef, useState } from "react";
import { TabbedInterface } from "../../../../components/layout/tabbed-interface/TabbedInterface";
import { Order } from "../../../orders/utils/types";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useOrderProfitHeader } from "./useOrderProfitHeader";
import { useOrderProfitTabs } from "./useOrderProfitTabs";
import {
  PROFIT_MODE,
  ProfitMode,
  ProfitFormValues,
  RegisterSaveFn,
} from "./types";
import { HttpError, useCustomMutation } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  Order: Order;
};

export const OrderProfit = ({ Order }: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.profit",
  );

  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const queryClient = useQueryClient();

  const { mutate } = useCustomMutation<Order, HttpError, ProfitFormValues>();

  const [activeTab, setActiveTab] = useState<ProfitMode>(PROFIT_MODE.ACTUAL);
  const [editMode, setEditMode] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveRef = useRef<RegisterSaveFn | null>(null);
  const resetRef = useRef<(() => void) | null>(null);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const handleRegisterSave = useCallback((fn: RegisterSaveFn) => {
    saveRef.current = fn;
    // 👇 reset fonksiyonunu da extract et
    const result = fn(() => {});
    resetRef.current = result.reset;
  }, []);

  const { tabs, shouldShowOverriddenTab, shouldShowActualTab } =
    useOrderProfitTabs(Order, editMode, handleRegisterSave, handleDirtyChange);

  const handleCancel = useCallback(() => {
    resetRef.current?.();
    setEditMode(false);
    setIsDirty(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedCountry?.id) return;
    if (!saveRef.current) return;
    if (!isDirty) return;
    if (isSaving) return;

    // 1) Payload al + reset fonksiyonunu kap
    let payload: ProfitFormValues | null = null;

    const { reset: resetForm } = saveRef.current((p) => {
      payload = p;
    });

    if (!payload) return;

    setIsSaving(true);

    mutate(
      {
        url: `store/${selectedCountry.id}/order/${Order.id}/finance`,
        method: "put",
        values: payload,
        successNotification: () => ({
          message: translate("updatedFinanceSuccessfully"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          (async () => {
            const queryKey = [
              "order-detail",
              selectedCountry.id,
              String(Order.id),
            ];

            await queryClient.invalidateQueries({ queryKey });
            await queryClient.refetchQueries({ queryKey });

            resetForm();
            setEditMode(false);
            setIsDirty(false);
            setIsSaving(false);
          })();
        },
        onError: () => {
          setIsSaving(false);
        },
      },
    );
  }, [
    Order.id,
    isDirty,
    isSaving,
    mutate,
    queryClient,
    selectedCountry?.id,
    translate,
  ]);

  const { headerActions } = useOrderProfitHeader({
    activeTab,
    shouldShowOverriddenTab,
    shouldShowActualTab,
    translateWithoutPrefix,
    editMode,
    setEditMode,
    onSave: handleSave,
    onCancel: handleCancel,
    isDirty,
    isSaving,
  });

  return (
    <TabbedInterface
      tabs={tabs}
      defaultPath="actual"
      translate={(key) =>
        translate(
          {
            title: "title",
            [PROFIT_MODE.ACTUAL]: "tabs.actual",
            [PROFIT_MODE.ESTIMATED]: "tabs.estimated",
            [PROFIT_MODE.OVERRIDDEN]: "tabs.overridden",
          }[key as ProfitMode] ?? key,
        )
      }
      tabsVariant="fullWidth"
      headerActions={headerActions}
      persistTabs={false}
      onActiveTabChange={(path) => {
        setActiveTab(path as ProfitMode);
        if (path === PROFIT_MODE.ESTIMATED) {
          setEditMode(false);
          resetRef.current?.();
          setIsDirty(false);
        }
      }}
    />
  );
};
